import { modal } from 'components/Modals/ModalManager';
import {
  getMuscleHeatmapDataForExercises,
  MuscleGroup,
  MuscleHeatmapData,
  Routine,
  calculateSetCountPerMuscleGroups,
  getEstimatedExercisesDurationSeconds,
} from 'hevy-shared';
import { makeAutoObservable } from 'mobx';
import toast from 'react-hot-toast';
import { stores } from 'state/stores';
import API from 'utils/API';
import { fireAndForget } from 'utils/async';
import { sendEvent } from 'utils/events';
import { SupersetGroups } from 'components/ExerciseSummary/types';
import { exercisesToSupersetGroups } from '../../components/ExerciseSummary/utils';
import { log } from 'utils/log';
import { copyToClipboard } from 'utils/clipboard';
import { ExerciseTemplate } from 'exercises/types';
import { t } from 'translations/t';
import { secondsToWordFormatMinutesLocalized } from 'translations/units';

interface Props {
  routineId: string | null;
  isAuthenticated: boolean;
}

export class RoutineDetailViewModel {
  routine?: Routine;
  isAuthenticated: boolean;
  isError: boolean = false;
  isInitialLoading: boolean = true;

  constructor({ routineId, isAuthenticated }: Props) {
    log('RoutineDetailViewModel constructor');

    makeAutoObservable(this);

    this.isAuthenticated = isAuthenticated;

    if (routineId) {
      this.fetch(routineId);
    } else {
      this.isError = true;
    }
  }

  get existingUserSaveButtonTitle(): string {
    return this.isRoutineSaved
      ? t('web.folderDetail.saveButton.saved')
      : t('web.routineDetail.saveButton.saveRoutine');
  }

  get isRoutineSaved(): boolean {
    const routineId = this.routine?.id;
    if (!routineId) return false;

    return !!stores.routines.routines.find(r => r.parent_routine_id === routineId);
  }

  private fetch = async (routineId: string) => {
    try {
      // The mobile app uses this same logic, it's a bit cringe but I'd rather
      // not break something just because of this one screen in the web app.
      const result =
        routineId.length === 11
          ? await API.getRoutineWithShortId(routineId)
          : await API.getRoutine(routineId);
      this.routine = result.data.routine;
      this.isError = false;
    } catch {
      this.isError = true;
    } finally {
      this.isInitialLoading = false;
    }
  };

  onExistingUserSaveRoutine = async () => {
    const routineId = this.routine?.id;
    if (!routineId) return;

    if (!stores.subscription.isPro && stores.routines.routines.length >= 4) {
      modal.paywall({
        type: 'routine-limit',
        onCancel: () => null,
      });
      return;
    }

    const save = () => {
      return toast.promise(
        (async () => {
          await API.postRoutineCopy({
            routineId,
            ignoreValues: true,
            index: 0,
            folderId: null,
          });
          await fireAndForget([stores.routines.fetch()]);
        })(),
        {
          loading: t('web.routineDetail.saveRoutine.loading'),
          success: t('web.routineDetail.saveRoutine.success'),
          error: t('web.routineDetail.saveRoutine.error'),
        },
      );
    };

    if (this.isRoutineSaved) {
      modal.confirmation({
        title: t('web.routineDetail.saveRoutineAgain.title'),
        subtitle: t('web.routineDetail.saveRoutineAgain.subtitle'),
        submitButtonTitle: t('web.folderDetail.saveFolderAgain.submitButton'),
        onConfirm: save,
      });
    } else {
      save();
    }
  };

  onCopyUrl = () => {
    sendEvent('routineDetail_copyLink', { routineId: this.routineId });
    copyToClipboard(window.location.href);
    toast.success(t('global.copiedToClipboard'));
  };

  onDeleteRoutine = () => {
    if (!this.routine) return;

    modal.desctructiveConfirmation({
      title: t('web.routineDetail.deleteRoutine.title', { routineTitle: this.routine.title }),
      subtitle: t('web.routineDetail.deleteRoutine.subtitle'),
      submitButtonTitle: t('web.routineDetail.deleteRoutine.submitButton'),
      onConfirm: async () => {
        await toast.promise(
          (async () => {
            await API.deleteRoutine(this.routineId);
            await fireAndForget([stores.routines.fetch()]);
          })(),
          {
            loading: t('web.routineDetail.deleteRoutine.loading'),
            success: t('web.routineDetail.deleteRoutine.success'),
            error: t('web.routineDetail.deleteRoutine.error'),
          },
          {
            success: {
              icon: '🔥',
            },
          },
        );

        sendEvent('routineDetail_deleteRoutine_complete');
      },
    });
  };

  get routineId() {
    return this.routine?.id || '';
  }

  get isUsersRoutine() {
    if (stores.auth.isAuthenticated) {
      return stores.account.username === this.routine?.username;
    } else {
      return false;
    }
  }

  get title(): string {
    return this.routine?.title || '';
  }

  get routineOwnerProfilePic(): string | undefined {
    return this.routine?.profile_pic ?? undefined;
  }

  get username(): string {
    return this.routine?.username || '';
  }

  get supersetGroups(): SupersetGroups {
    if (!this.routine) return [];

    return exercisesToSupersetGroups(this.routine);
  }

  get setCountPerMuscleGroup(): Record<MuscleGroup, number> {
    if (!this.routine) return {} as Record<MuscleGroup, number>;

    const exercises = this.routine?.exercises.map(exercise => {
      const template = stores.exerciseTemplates.getExercise(exercise.exercise_template_id);
      if (!template) return null;
      return {
        exerciseTemplate: template,
        sets: exercise.sets,
      };
    });

    return calculateSetCountPerMuscleGroups({
      workoutsOrRoutines: [
        {
          exercises: exercises
            .filter(e => e !== null)
            .map(e => ({
              muscle_group: e.exerciseTemplate.muscle_group,
              other_muscles: e.exerciseTemplate.other_muscles ?? [],
              sets: e.sets,
            })),
        },
      ],
      includeWarmupSets: stores.userPreferences.volumeIncludesWarmupSets,
    });
  }

  get heatmapData(): MuscleHeatmapData {
    if (!this.routine) return {} as MuscleHeatmapData;

    const exercisesForHeatmap = this.routine?.exercises
      .filter(exercise =>
        stores.userPreferences.volumeIncludesWarmupSets
          ? true
          : !exercise.sets.every(s => s.indicator === 'warmup'),
      )
      .map(exercise => stores.exerciseTemplates.getExercise(exercise.exercise_template_id))
      .filter((exercise): exercise is ExerciseTemplate => !!exercise)
      .map(exercise => ({
        muscle_group: exercise.muscle_group,
        other_muscles: exercise.other_muscles ?? [],
      }));

    return getMuscleHeatmapDataForExercises(exercisesForHeatmap);
  }

  get routineSummaryExerciseCount(): number {
    return this.routine?.exercises.length ?? 0;
  }

  get routineSummarySetCount(): number {
    const setCount = this.routine?.exercises.flatMap(e =>
      e.sets.filter(s =>
        stores.userPreferences.volumeIncludesWarmupSets ? true : s.indicator !== 'warmup',
      ),
    ).length;
    return setCount ?? 0;
  }

  get routineSummaryEstimatedDuration(): string {
    if (!this.routine) return '00:00';

    const duration = getEstimatedExercisesDurationSeconds({
      exercises: this.routine?.exercises.map(e => {
        return {
          rest_seconds: e.rest_seconds ?? null,
          exercise_type: e.exercise_type,
          sets: e.sets.map(s => {
            return {
              duration_seconds: s.duration_seconds ?? null,
              indicator: s.indicator,
            };
          }),
        };
      }),
    });

    return secondsToWordFormatMinutesLocalized(duration);
  }
}
